import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { SdiArrow } from '@sdir/sds';
import { hasStatusEmoji, UserViewTexts } from '../../../services/certreq/userView/texts';
import { UserStatus } from '../../../services/certreq/userView/userStatus';
import {
  groupLogicText,
  nodeText,
  UserBundleNode,
  UserChoiceNode,
  UserNode,
  UserRequirementNode,
} from '../../../services/certreq/userView/userRequirements';
import { RequirementDeclaration } from './RequirementDeclaration';
import { RequirementUpload } from './RequirementUpload';
import { UserStatusEmoji, UserStatusText } from './UserStatusBadge';
import { statusVariables, USER_STATUSES } from './userStatusColors';

/**
 * Hvor tett noden pakkes.
 *
 * Alle tre tegnes med samme flate — en tintet boks i statusens farge — slik at
 * en rad ser lik ut uansett hvor dypt i visningen den ligger. Varianten avgjør
 * bare luften:
 *
 * - `card` — kravene som ligger rett på flaten, med romsligst padding.
 * - `row` — kravene under en gruppe i basisseksjonen, strammere pakket.
 * - `bare` — kravene inne i en alternativboks. Samme tetthet som `row`; navnet
 *   er beholdt fordi de to nivåene skal kunne skilles igjen.
 */
export type NodeVariant = 'card' | 'row' | 'bare';

/**
 * Skal raden vise ansiktet sitt?
 *
 * To ting avgjør det. Statusen må ha et ansikt i det hele tatt —
 * `notApplicable` har ingen, en vei brukeren ikke følger skal være stille — og
 * raden må stå **fritt**. Kravene inne i en åpnet gruppe dropper ansiktet: der
 * er det gruppelinja over dem som bærer statusen utad, og tjue små ansikter
 * nedover en åpnet gruppe gjør lista tyngre å lese.
 *
 * Statusen forsvinner ikke med ansiktet. Ordet ligger fortsatt visuelt skjult
 * på hver eneste rad, så en skjermleser hører «Mangler» like fullt.
 */
const showsEmoji = (status: UserStatus, inGroup: boolean): boolean =>
  !inGroup && hasStatusEmoji(status);

/**
 * En stabil id til `aria-controls` på chevronen. Løpenummeret holder i en
 * klient uten server-rendering, og komponenten beholder sin id gjennom hele
 * livet — til forskjell fra en id utledet av kravnøkkelen, som ikke er unik når
 * det samme kravet står i to alternativer.
 */
let nextGroupId = 0;

const useGroupItemsId = (): string => {
  const [id] = useState(() => {
    nextGroupId += 1;
    return `certreq-group-items-${nextGroupId}`;
  });
  return id;
};

interface NodeTitleProps {
  node: UserNode;
}

/**
 * Radens tekst, med kravtypen som fet ledetekst foran: «**Fartstid:** 1080
 * dager fartstid som overstyrmann».
 *
 * Ledeteksten forteller hva slags dokumentasjon raden gjelder før søkeren har
 * lest den, og gjør en lang liste mulig å skumme. Hvilken tekst det er — og om
 * det er noen i det hele tatt — er modellens svar (`node.typeLabel`), ikke
 * komponentens.
 *
 * Teksten står i sitt eget element ved siden av ledeteksten. Det er kravets
 * egen tekst — den samme `nodeText()` bruker til å navngi knappene — og skal
 * kunne leses, og testes, uten ledeteksten foran seg.
 */
const NodeTitle: React.FC<NodeTitleProps> = ({ node }) => (
  <Title>
    {node.typeLabel && <TypeLabel>{node.typeLabel}: </TypeLabel>}
    <TitleText>{nodeText(node)}</TitleText>
  </Title>
);

interface RowProps {
  node: UserRequirementNode;
  variant: NodeVariant;
  /** Står raden inne i en gruppe? Da bærer gruppelinja ansiktet, ikke raden. */
  inGroup: boolean;
  certificateCode: string;
  texts: UserViewTexts;
}

/**
 * Ett krav: status, tekst, flate i statusfargen — og opplasting der modellen
 * har lagt den. Kravet står på én linje, uten begrunnelse; søkervisningen skal
 * kunne skannes på sekunder.
 *
 * Linja er kravets **beskrivelse** når det har en — den er skrevet for søkeren
 * («Kurset må være fullført og gyldig»), mens navnet ofte er kildedataenes
 * stikkord. Valget er `nodeText()` sitt, ikke komponentens.
 *
 * **Ansiktet står sist i DOM-en, men først på raden.** Rutenettet setter det i
 * venstre kolonne, mens statusordet ligger visuelt skjult først i linja. At de
 * to kan ligge hver sin plass er hele grunnen til at de er skilt: emojien er
 * ren dekorasjon og kan plasseres etter øyet, ordet leses av skjermleseren og
 * må ligge der lesningen begynner.
 */
const RequirementRow: React.FC<RowProps> = ({ node, variant, inGroup, certificateCode, texts }) => {
  const emoji = showsEmoji(node.status, inGroup);

  return (
    <Node
      data-status={node.status}
      data-variant={variant}
      data-emoji={String(emoji)}
      data-testid={`requirement-${node.requirementId || 'uten-id'}`}
    >
      <Header>
        <UserStatusText status={node.status} />
        <NodeTitle node={node} />
      </Header>

      {/*
        Erklæringen besvares med en avkryssing, ikke med en fil. Den står også
        på en vei brukeren ikke følger, slik at hun kan bytte vei uten å måtte
        angre den forrige først.
      */}
      {node.declaration ? (
        <RequirementDeclaration node={node} certificateCode={certificateCode} texts={texts} />
      ) : (
        (node.upload || node.files.length > 0) && (
          <RequirementUpload
            node={node}
            certificateCode={certificateCode}
            texts={texts}
            showButton={node.upload}
          />
        )
      )}

      {emoji && <UserStatusEmoji status={node.status} />}
    </Node>
  );
};

interface GroupProps {
  node: UserBundleNode | UserChoiceNode;
  variant: NodeVariant;
  /** Ligger gruppen selv inne i en annen gruppe? */
  inGroup: boolean;
  certificateCode: string;
  texts: UserViewTexts;
}

/**
 * En gruppe («minst ett av», «alle disse») — én dokumentasjonsenhet.
 *
 * Gruppen står **kollapset**, og linja er bygget som en kravrad: statusordet,
 * så gruppens egen tekst («Gyldig ROC eller GOC, STCW regel IV/2.»), og
 * chevronen ytterst til høyre. Da er venstrekanten den samme på alle rader av
 * seg selv, og kravradene uten chevron trenger ikke et plassholder-innrykk.
 *
 * Opplastingen ligger på egen linje under, og står synlig også når gruppen er
 * lukket: brukeren må kunne levere, og se hva hun allerede har lastet opp, uten
 * å åpne gruppen først.
 *
 * Logikken bak gruppen står ikke på den kollapsede linja: «Minst ett av»
 * gjentok seg nedover hele siden uten å si søkeren noe. Den kommer fram når
 * gruppen åpnes, som en ledetekst over kravene — da er den nettopp det brukeren
 * trenger for å lese lista riktig.
 *
 * Kravene blir liggende i DOM-en og skjules med `hidden`, slik at
 * `aria-controls` peker på noe som finnes.
 */
const GroupSection: React.FC<GroupProps> = ({ node, variant, inGroup, certificateCode, texts }) => {
  const [expanded, setExpanded] = useState(false);
  const itemsId = useGroupItemsId();
  const items = node.kind === 'bundle' ? node.items : node.options;
  const emoji = showsEmoji(node.status, inGroup);
  const text = nodeText(node);

  return (
    <Node
      as="section"
      data-status={node.status}
      data-variant={variant}
      data-emoji={String(emoji)}
      data-testid={`requirement-group-${node.requirementId || 'uten-id'}`}
    >
      <Header>
        <UserStatusText status={node.status} />
        <NodeTitle node={node} />
        <Toggle
          type="button"
          aria-expanded={expanded}
          aria-controls={itemsId}
          aria-label={expanded ? texts.collapseGroup(text) : texts.expandGroup(text)}
          onClick={() => setExpanded((open) => !open)}
        >
          <SdiArrow
            size="s"
            height="0.9em"
            color="currentColor"
            direction={expanded ? 'up' : 'down'}
          />
        </Toggle>
      </Header>

      {node.description && <Description>{node.description}</Description>}

      {(node.upload || node.files.length > 0) && (
        <RequirementUpload
          node={node}
          certificateCode={certificateCode}
          texts={texts}
          showButton={node.upload}
        />
      )}

      {/*
        Kravene arver gruppas kontekst: inne i en alternativboks skal de være
        like nakne som gruppa selv. Ledeteksten over dem forteller hvordan de
        henger sammen — alle, eller minst ett.
      */}
      <Nested id={itemsId} hidden={!expanded}>
        <Logic>{groupLogicText(node, texts)}</Logic>
        <UserNodeList
          nodes={items}
          variant={variant === 'bare' ? 'bare' : 'row'}
          inGroup
          certificateCode={certificateCode}
          texts={texts}
        />
      </Nested>

      {emoji && <UserStatusEmoji status={node.status} />}
    </Node>
  );
};

interface UserNodeListProps {
  nodes: UserNode[];
  /** Ramme eller kompakt rad. Standard er ramme — kravene rett på flaten. */
  variant?: NodeVariant;
  /**
   * Ligger lista inne i en gruppe som er hentet fram?
   *
   * Da dropper radene ansiktet sitt — gruppelinja over dem bærer statusen — og
   * flagget følger videre nedover. Det er `variant` som styrer tettheten; dette
   * styrer hvem som viser status utad, og de to faller ikke sammen: kravene
   * rett i en alternativboks er `bare`, men står fritt og beholder ansiktet.
   */
  inGroup?: boolean;
  certificateCode: string;
  texts: UserViewTexts;
}

/** Tegner en liste av krav-noder etter hverandre. */
export const UserNodeList: React.FC<UserNodeListProps> = ({
  nodes,
  variant = 'card',
  inGroup = false,
  certificateCode,
  texts,
}) => (
  <List data-variant={variant}>
    {nodes.map((node) =>
      node.kind === 'requirement' ? (
        <RequirementRow
          key={node.key}
          node={node}
          variant={variant}
          inGroup={inGroup}
          certificateCode={certificateCode}
          texts={texts}
        />
      ) : (
        <GroupSection
          key={node.key}
          node={node}
          variant={variant}
          inGroup={inGroup}
          certificateCode={certificateCode}
          texts={texts}
        />
      )
    )}
  </List>
);

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;

  /* Radene ligger tettere enn kortene — de står alltid inne i noe annet. */
  &[data-variant='row'],
  &[data-variant='bare'] {
    gap: 0.65rem;
  }
`;

/**
 * Statusvariablene, én blokk per status. De settes én gang på noden — flate,
 * kant, og fargene opplastingsknappen låner — og leses videre av reglene under
 * og av `RequirementUpload`. Hvilken familie hver status låner fra, står i
 * `userStatusColors.ts`.
 */
const statusBlocks = css`
  ${({ theme }) =>
    USER_STATUSES.map(
      (status) => `
        &[data-status='${status}'] {
          ${statusVariables(theme, status)}
        }
      `
    ).join('\n')}
`;

/**
 * Selve flaten, og radens grunnform: **to kolonner**.
 *
 * Venstre kolonne er ansiktets, og bare ansiktets. Alt annet — linja med
 * radteksten, beskrivelsen, opplastingsknappen, fillista og kravene under en
 * åpnet gruppe — ligger i høyre kolonne, og kan derfor aldri legge seg under
 * ansiktet. Det er hele poenget med å bruke et rutenett her: kolonnen er
 * reservert, ikke bare unngått.
 *
 * Rutenettet gjør også plasseringen uavhengig av rekkefølgen i DOM-en.
 * Ansiktet står **sist** blant barna — det er ren dekorasjon, og skal ikke
 * ligge foran innholdet en skjermleser leser — men tegnes i første rad av
 * venstre kolonne.
 *
 * Rader uten ansikt — kravene inne i en åpnet gruppe, og «Ikke aktuelt», som
 * ikke har noe ansikt i det hele tatt — trenger ingen reservert kolonne. Da
 * faller den bort, slik at teksten begynner på nodens egen venstrekant.
 */
const Node = styled.article`
  ${statusBlocks}

  /*
   * Statuskolonnens bredde. Den skal romme ansiktet OG luften mellom det og
   * kravteksten — ansiktet står helt til venstre i kolonnen, så alt som blir til
   * overs er selve mellomrommet. Vokser ansiktet, må tallet vokse med det, ellers
   * klemmer glyffen seg inntil teksten.
   */
  --status-gutter: 4.2rem;

  display: grid;
  grid-template-columns: var(--status-gutter) minmax(0, 1fr);
  align-items: start;
  border: 1px solid var(--status-border);
  border-radius: ${({ theme }) => theme.card.borderRadius};
  background-color: var(--status-surface);

  & > *:not([data-status-emoji]) {
    grid-column: 2;
    min-width: 0;
  }

  & > [data-status-emoji] {
    grid-column: 1;
    grid-row: 1;
  }

  &[data-emoji='false'] {
    --status-gutter: 0rem;
  }

  /* Kravet inngår ikke i veien brukeren følger — det tones ned, ikke skjules. */
  &[data-status='notApplicable'] {
    opacity: 0.6;
  }

  &[data-variant='card'] {
    padding: 1.45rem 1.65rem;
  }

  &[data-variant='row'],
  &[data-variant='bare'] {
    padding: 1.2rem 1.4rem;
  }
`;

/**
 * Linja: [tekst] … [chevron]. Ansiktet står utenfor den, i venstrekolonnen.
 *
 * Chevronen står **ytterst til høyre**, ikke foran teksten: her ligger
 * ekspanderbare grupper og vanlige kravrader om hverandre, og med pila til
 * venstre måtte hver eneste kravrad få et plassholder-innrykk for å stå på
 * linje med gruppene.
 */
const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

/**
 * All linjetekst — kravrader og gruppelinjer — deler denne stilen. Samme
 * størrelse og **samme vekt** uansett hvor linja står og hvilken status den
 * har: en gruppe er ikke en overskrift over kravene sine, den er en linje på
 * lik linje med dem. Hierarkiet ligger i flaten og innrykket, ikke i
 * skriftvekten.
 */
const Title = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  line-height: 1.35;
  color: var(--status-text);
  overflow-wrap: anywhere;
`;

/**
 * Kravtypen som ledetekst. Den er tyngre enn radteksten, ikke større og ikke i
 * en annen farge — den skal kunne skummes nedover venstresiden av lista uten å
 * bli en egen kolonne med eget fargespråk. Den henger sammen med teksten sin og
 * brytes sammen med den.
 */
const TypeLabel = styled.strong`
  font-weight: 700;
`;

/** Kravets egen tekst, uten ledeteksten foran seg. */
const TitleText = styled.span`
  overflow-wrap: anywhere;
`;

/**
 * Gruppens beskrivelse — forklaringen på hva valget står mellom. Mindre og
 * dusere enn linjeteksten, og bare på grupper: på et enkeltkrav er beskrivelsen
 * selve radteksten, ikke en linje under.
 */
const Description = styled.p`
  margin: 0.3rem 0 0;
  font-size: 14px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.font.text75};
`;

/**
 * «Minst ett av disse» / «Alle disse» — ledeteksten som forteller hvordan
 * kravene under gruppen henger sammen. Den vises bare når gruppen er åpnet, og
 * skal lede lista, ikke konkurrere med den.
 */
const Logic = styled.p`
  margin: 0 0 0.35rem;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.font.text75};
`;

/**
 * Chevronen som åpner og lukker gruppen. Knappen er ren pil: navnet ligger i
 * `aria-label`, som sier hvilken gruppe den gjelder. `margin-inline-start: auto`
 * skyver den helt ut til høyre kant av raden, også når radteksten er kort.
 */
const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  flex: 0 0 auto;
  margin-inline-start: auto;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  cursor: pointer;
  color: var(--status-text);

  &:hover {
    background: rgb(0 0 0 / 6%);
  }
`;

/** Underradene rykkes inn under gruppa si, så det syns at de hører til den. */
const Nested = styled.div`
  margin-top: 0.6rem;
  padding-inline-start: 0.9rem;

  &[hidden] {
    display: none;
  }
`;

export default UserNodeList;
